import { useEffect, useState, useCallback } from "react";
import { Button } from "../Base/Button";
import { Popup } from "../Base/Popup";
import type {
  AgreementConfirmationProps,
  AgreementConfirmationConfig,
} from "./AgreementConfirmation.types";
import "./AgreementConfirmation.scss";
import { agreementConfirmationApi } from "../../api/agreementConfirmation";
import { useT } from "../../i18n/hooks";
import { ProtocolDialog } from "../ProtocolDialog/ProtocolDialog";
import { AGREEMENT_TRIGGER_EVENT } from "./triggerAgreementConfirmation";

/**
 * 协议确认弹窗内容组件
 */
const AgreementConfirmationContent: React.FC<{
  onComplete: () => void;
  config: AgreementConfirmationConfig;
  onConfirm?: () => void;
  onCancel?: () => void;
}> = ({ onComplete, config, onConfirm, onCancel }) => {
  const t = useT();
  const [visible, setVisible] = useState(true);
  const [protocolDialogVisible, setProtocolDialogVisible] = useState(false);
  const [currentProtocolPath, setCurrentProtocolPath] = useState("");

  const parseMarkdownLinks = (text: string): string => {
    return text.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="javascript:void(0)" data-protocol-path="$2">$1</a>'
    );
  };

  const handleProtocolLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A" && target.hasAttribute("data-protocol-path")) {
      e.preventDefault();
      const path = target.getAttribute("data-protocol-path");
      if (path) {
        setCurrentProtocolPath(path);
        setProtocolDialogVisible(true);
      }
    }
  };

  const handleAgree = useCallback(() => {
    setVisible(false);
    onConfirm?.();
    onComplete();
  }, [onConfirm, onComplete]);

  const handleCancel = useCallback(() => {
    setVisible(false);
    onCancel?.();
    onComplete();
  }, [onCancel, onComplete]);

  const agreementHtml = config.agreementText
    ? parseMarkdownLinks(config.agreementText)
    : "";

  return (
    <>
      <Popup
        visible={visible}
        onMaskClick={handleCancel}
        bodyStyle={{
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div className="agreement-confirmation">
          {config.agreementText && (
            <div
              className="agreement-confirmation__content"
              onClick={handleProtocolLinkClick}
              dangerouslySetInnerHTML={{ __html: agreementHtml }}
            />
          )}

          <div className="agreement-confirmation__buttons">
            <Button
              variant="primary"
              className="agreement-confirmation__button agreement-confirmation__button--primary"
              onClick={handleAgree}
            >
              {t("common.agree")}
            </Button>
            <Button
              variant="cancel"
              className="agreement-confirmation__button agreement-confirmation__button--secondary"
              onClick={handleCancel}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      </Popup>

      <ProtocolDialog
        visible={protocolDialogVisible}
        onClose={() => setProtocolDialogVisible(false)}
        protocolPath={currentProtocolPath}
      />
    </>
  );
};

/**
 * 协议确认弹窗组件
 *
 * 使用方式：
 * 1. 前置弹窗模式：由 PrerequisitePopups 驱动，传入 onComplete，
 *    配置启用时弹出弹窗，由调用方决定是否再次弹出（不做 sessionStorage 缓存）
 * 2. 事件模式：通过 triggerAgreementConfirmation() 触发（登录/注册后）
 * 3. 受控模式：传入 visible、onConfirm、onCancel props 直接控制
 */
export const AgreementConfirmation: React.FC<
  AgreementConfirmationProps & { onComplete?: () => void }
> = ({
  visible: controlledVisible,
  onConfirm: controlledOnConfirm,
  onCancel: controlledOnCancel,
  onComplete,
}) => {
  const [config, setConfig] = useState<AgreementConfirmationConfig | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  // 事件模式状态
  const [eventVisible, setEventVisible] = useState(false);
  const [eventCallbacks, setEventCallbacks] = useState<{
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({});
  const [pendingCallbacks, setPendingCallbacks] = useState<{
    onConfirm?: () => void;
    onCancel?: () => void;
  } | null>(null);

  const isControlled = controlledVisible !== undefined;
  const isPrerequisiteMode = !isControlled && onComplete !== undefined;

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await agreementConfirmationApi.getConfig();
        setConfig(data);
      } catch (error) {
        console.error("[AgreementConfirmation] Failed to load config:", error);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  // 前置弹窗模式：配置加载完成后决定是否显示
  // 使用 sessionStorage 缓存，同一个会话内弹过一次就不再弹
  useEffect(() => {
    if (!isPrerequisiteMode) return;
    if (loading) return;

    if (!config?.enabled) {
      onComplete?.();
    } else {
      const alreadyShown = sessionStorage.getItem("agreement_shown") === "1";
      if (alreadyShown) {
        onComplete?.();
      } else {
        setVisible(true);
      }
    }
  }, [loading, config?.enabled, isPrerequisiteMode, onComplete]);

  // 监听全局事件触发（登录/注册场景）
  useEffect(() => {
    if (isControlled) return;

    const handleTrigger = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { onConfirm, onCancel } = customEvent.detail || {};

      if (loading) {
        setPendingCallbacks({ onConfirm, onCancel });
        return;
      }

      if (!config || !config.enabled) {
        onConfirm?.();
        return;
      }

      setEventCallbacks({ onConfirm, onCancel });
      setEventVisible(true);
    };

    window.addEventListener(AGREEMENT_TRIGGER_EVENT, handleTrigger);
    return () => {
      window.removeEventListener(AGREEMENT_TRIGGER_EVENT, handleTrigger);
    };
  }, [isControlled, loading, config]);

  // 配置加载完成后处理待执行回调
  useEffect(() => {
    if (loading || !pendingCallbacks) return;

    if (!config || !config.enabled) {
      pendingCallbacks.onConfirm?.();
      setPendingCallbacks(null);
      return;
    }

    setEventCallbacks(pendingCallbacks);
    setEventVisible(true);
    setPendingCallbacks(null);
  }, [loading, config, pendingCallbacks]);

  // 受控模式
  if (isControlled) {
    if (!controlledVisible || !config?.enabled || loading) return null;
    return (
      <AgreementConfirmationContent
        onComplete={() => {}}
        config={config}
        onConfirm={controlledOnConfirm}
        onCancel={controlledOnCancel}
      />
    );
  }

  // 前置弹窗模式
  if (isPrerequisiteMode && visible && config?.enabled) {
    return (
      <AgreementConfirmationContent
        onComplete={() => {
          sessionStorage.setItem("agreement_shown", "1");
          setVisible(false);
          onComplete?.();
        }}
        config={config}
      />
    );
  }

  // 事件模式（登录/注册触发）
  if (eventVisible && config?.enabled) {
    return (
      <AgreementConfirmationContent
        onComplete={() => setEventVisible(false)}
        config={config}
        onConfirm={eventCallbacks.onConfirm}
        onCancel={eventCallbacks.onCancel}
      />
    );
  }

  return null;
};
